import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from '../../img/logo.png';
import { Typography } from "@mui/material";
import { subdomain, getBackendUrl } from '../../auth/utilities';
import cookie from "react-cookies";
import "./WhizzLearningLogin.css";

export default function WhizzLearningLogin() {
    const [focusedInput, setFocusedInput] = useState(null);
    const [staffEmail, setStaffEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [backendEmailError, setBackendEmailError] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [loadingVerify, setLoadingVerify] = useState(false);
    const [otpError, setOtpError] = useState("");
    const [timeLeft, setTimeLeft] = useState(600);
    const totalTime = 600;
    const [otpValue, setOtpValue] = useState("");
    const [loading, setLoading] = useState(false);
    const csrfToken = cookie.load("csrftoken");
    const navigate = useNavigate();
    const backend = getBackendUrl();

    const resetOtpProcess = () => {
        setOtpSent(false);
        setOtpValue("");
        setStaffEmail("");
        setTimeLeft(600);
    };

    useEffect(() => {
        if (otpSent && timeLeft === 0) {
            resetOtpProcess();
        }
    }, [timeLeft, otpSent]);

    const progress = (timeLeft / totalTime) * 100;
    const formatTime = (seconds) => {
        const m = String(Math.floor(seconds / 60)).padStart(2, "0");
        const s = String(seconds % 60).padStart(2, "0");
        return `${m}:${s}`;
    };

    useEffect(() => {
        let timer;
        if (otpSent && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [otpSent, timeLeft]);

    const validateEmail = (value) => {
        if (!value.trim()) {
            setEmailError("Email is required");
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            setEmailError("Enter a valid email address");
            return false;
        }
        setEmailError("");
        return true;
    };

    useEffect(() => {
        const blockBack = () => {
            navigate("/whizz-learning/", { replace: true });
        };
        window.history.pushState(null, "", window.location.href);
        window.addEventListener("popstate", blockBack);
        return () => {
            window.removeEventListener("popstate", blockBack);
        };
    }, [navigate]);

    const handleGeneratingOtp = async () => {
        setBackendEmailError("");
        if (!validateEmail(staffEmail)) return;

        const payload = { staff_email: staffEmail };
        try {
            setLoading(true);
            const response = await fetch(
                `${backend}${subdomain}/en/trainingmodule/sendotp/`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify(payload),
                }
            );
            const data = await response.json();
            if (!response.ok) {
                setBackendEmailError(data?.error || "Failed to send OTP");
                return;
            }
            if (data?.message) {
                setOtpSent(true);
                setTimeLeft(600);
            }
        } catch (error) {
            setBackendEmailError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        setLoadingVerify(true);
        setOtpError("");
        const payload = { staff_email: staffEmail, otp: otpValue };
        try {
            const response = await fetch(
                `${backend}${subdomain}/en/trainingmodule/verifyotp/`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify(payload),
                }
            );
            const data = await response.json();
            if (response.ok && data?.message) {
                getTrainingList();
            } else {
                setOtpError(data?.error || "OTP verification failed");
            }
        } catch (error) {
            setOtpError("Something went wrong, please try again.");
        } finally {
            setLoadingVerify(false);
        }
    };

    const getTrainingList = async () => {
        try {
            const response = await fetch(
                `${backend}${subdomain}/en/trainingmodule/traininglist/`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                }
            );
            const data = await response.json();
            if (response.ok && data.status !== "error") {
                sessionStorage.setItem("trainingList", JSON.stringify(data.trainings || []));
                navigate("/whizz-learning/training-list/", {
                    state: { trainings: data.trainings || [] },
                });
            } else if (data.status === "error" || data.error) {
                setOtpError(data.message || data.error || "Failed to load training list.");
            }
        } catch (err) {
            console.log("Network Error:", err);
            setOtpError("An error occurred while loading the training list.");
        }
    };

    return (
        <div
            className="whizz-learning-login-container"
            onKeyDown={(e) => {
                if (e.key === "Enter" && !loading && !loadingVerify) {
                    if (otpSent) { handleVerifyOtp(); } else { handleGeneratingOtp(); }
                }
            }}>

            {/* LEFT — Login Form */}
            <div className="login-left-section">
                <div className="login-left-decor"></div>
                <div className="login-form-container">
                    <div className="login-form-box">
                        <h1 className="login-heading">Credentials, Please!</h1>
                        <p className="login-subheading">Unlock the door to smarter compliance vibes</p>

                        <div>
                            <div className="login-input-group">
                                <label className="login-label">UserEmail</label>
                                <input
                                    type="text"
                                    placeholder="Enter your Email"
                                    value={staffEmail}
                                    readOnly={otpSent}
                                    onChange={(e) => {
                                        setStaffEmail(e.target.value);
                                        if (emailError) validateEmail(e.target.value);
                                    }}
                                    onBlur={() => validateEmail(staffEmail)}
                                    onFocus={() => setFocusedInput("userEmail")}
                                    className={`login-input ${focusedInput === "userEmail" ? "focused" : ""} ${emailError ? "error" : ""}`}
                                    style={{ borderColor: emailError || focusedInput === "userEmail" ? undefined : "#ccc" }}
                                />
                                {(emailError || backendEmailError) && (
                                    <div className="login-error-text">
                                        {emailError || backendEmailError}
                                    </div>
                                )}

                                {otpSent && (
                                    <div className="login-change-email-box">
                                        <span className="login-change-email-text">Entered wrong email? </span>
                                        <span
                                            role="button"
                                            className="login-change-email-link"
                                            onClick={resetOtpProcess}
                                        >
                                            Use a different email
                                        </span>
                                    </div>
                                )}

                                {otpSent && (
                                    <>
                                        <label className="login-label">Enter OTP</label>
                                        <input
                                            type="text"
                                            placeholder="Enter OTP"
                                            value={otpValue}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d{0,6}$/.test(value)) {
                                                    setOtpValue(value);
                                                    setOtpError("");
                                                }
                                            }}
                                            onFocus={() => setFocusedInput("otp")}
                                            onBlur={() => setFocusedInput(null)}
                                            className={`login-input ${focusedInput === "otp" ? "focused" : ""} ${otpError ? "error" : ""}`}
                                        />
                                        {otpError && (
                                            <p className="login-error-text" style={{ marginTop: "6px", fontSize: "13px" }}>
                                                {otpError}
                                            </p>
                                        )}
                                    </>
                                )}

                                {otpSent && (
                                    <>
                                        <div className="otp-timer-container">
                                            OTP valid for:
                                            <span className="otp-timer-value">
                                                {formatTime(timeLeft)}
                                            </span>
                                        </div>
                                        <div className="otp-progress-bar">
                                            <div className="otp-progress-fill" style={{ width: `${progress}%` }}></div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={otpSent ? handleVerifyOtp : handleGeneratingOtp}
                                disabled={loading || loadingVerify}
                                className="login-button"
                            >
                                {loading ? "Sending OTP..." : loadingVerify ? "Verifying..." : otpSent ? "Verify OTP" : "Generate OTP"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT — Welcome Panel */}
            <div className="login-right-section">
                <div className="star star-1"></div>
                <div className="star star-2"></div>
                <div className="star star-3"></div>
                <div className="star star-4"></div>
                <div className="star star-5"></div>
                <div className="star star-6"></div>
                <div className="login-content-box">
                    <div className="login-logo-container">
                        <img src={logo} alt="logo" className="login-logo" />
                    </div>
                    <Typography className="login-welcome-heading">Welcome to WhizzLearning</Typography>
                    <p className="login-description">
                        WhizzLearning delivers engaging security and compliance training tailored for modern workplaces. Empower your team to confidently navigate regulations, safeguard critical data, and build a proactive security culture. Our interactive courses transform essential compliance topics into memorable learning experiences, helping employees stay sharp, secure, and compliant every day.
                    </p>
                </div>
            </div>
        </div>
    );
}
